/**
 * Feedback — every thumbs up and thumbs down from chat, one status per item.
 * Tabs split open, resolved, praise and noise; the category filter narrows
 * any tab. Open items close as resolved or noise, each with a note.
 * Mechanic's words lead; the triage analysis supports.
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  Modal,
  Select,
  Skeleton,
  Table,
  TextArea,
} from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { useMe } from '../../hooks/useMe.js';
import { formatDay, formatDayTime } from '../../lib/format.js';
import { canTriageFeedback } from '../../lib/permissions.js';
import type { FeedbackCounts, FeedbackItem, FeedbackStatus } from '../../lib/types.js';
import {
  useFeedbackCountsLogic,
  useFeedbackListLogic,
  useRecategorizeLogic,
  useSetFeedbackStatusLogic,
  type CloseAs,
  type FeedbackCategoryFilter,
} from './useFeedbackLogic.js';
import { TranscriptView } from './TranscriptView.js';

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All categories' },
  { value: 'model_behavior', label: 'Model behavior' },
  { value: 'code_bug', label: 'Code bug' },
  { value: 'data_gap', label: 'Data gap' },
  { value: 'knowledge_base', label: 'Knowledge base' },
  { value: 'praise', label: 'Praise' },
  { value: 'noise', label: 'Noise' },
];

const TABS: { id: FeedbackStatus; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'praise', label: 'Praise' },
  { id: 'noise', label: 'Noise' },
];

const CLOSE_COPY: Record<CloseAs, { title: string; label: string; action: string }> = {
  resolved: { title: 'Resolve', label: 'What we changed', action: 'Resolve' },
  noise: { title: 'Dismiss', label: 'Why it’s noise', action: 'Dismiss' },
};

const severityTone = (severity: string | null): string =>
  severity === 'high'
    ? 'bg-danger-soft text-danger'
    : severity === 'medium'
      ? 'bg-warning-soft text-warning'
      : 'bg-neutral-soft text-ink-muted';

const categoryLabel = (c: FeedbackItem['triageCategory']): string =>
  CATEGORY_OPTIONS.find((o) => o.value === c)?.label ?? 'Untriaged';

const quote = (item: FeedbackItem): string =>
  item.comment
    ? `“${item.comment}”`
    : `${item.rating === 'down' ? 'Thumbs down' : 'Thumbs up'}, no comment`;

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.1"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const ThumbIcon = ({ rating }: { rating: FeedbackItem['rating'] }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    role="img"
    aria-label={rating === 'down' ? 'Thumbs down' : 'Thumbs up'}
    className={rating === 'up' ? 'rotate-180' : undefined}
  >
    <path d="M17 14V2" />
    <path d="M9 18.12L10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
  </svg>
);

/** Close dialog — open-prop driven per the house modal rule. */
const CloseDialog = ({
  target,
  onClose,
}: {
  target: { item: FeedbackItem; as: CloseAs } | null;
  onClose: () => void;
}) => {
  const setStatus = useSetFeedbackStatusLogic();
  const [note, setNote] = useState('');
  const copy = CLOSE_COPY[target?.as ?? 'resolved'];
  const close = () => {
    setNote('');
    setStatus.reset();
    onClose();
  };
  const submit = async () => {
    if (!target || note.trim().length === 0) return;
    try {
      await setStatus.mutateAsync({
        feedbackId: target.item.id,
        status: target.as,
        note: note.trim(),
      });
      close();
    } catch {
      // surfaced via setStatus.error
    }
  };
  return (
    <Modal
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <Modal.Header>
        <Modal.Title>{copy.title}</Modal.Title>
        <Modal.CloseButton />
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-3.5">
          {setStatus.error && (
            <Banner variant="danger">
              <Banner.Body>
                <Banner.Title>{setStatus.error.message}</Banner.Title>
              </Banner.Body>
            </Banner>
          )}
          {target && <p className="text-[15px] font-semibold text-ink">{quote(target.item)}</p>}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="close-note"
              className="text-xs font-semibold uppercase tracking-[0.06em] text-ink-subtle"
            >
              {copy.label}
            </label>
            <TextArea
              id="close-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
            <span className="text-xs text-ink-subtle">
              Required. It shows on the item and in feedback reports.
            </span>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={close} disabled={setStatus.isPending}>
          Cancel
        </Button>
        <Button
          onClick={() => void submit()}
          disabled={setStatus.isPending || note.trim().length === 0}
        >
          {copy.action}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/** Re-categorize options — the category IS the route (KB queues the handoff). */
const RECATEGORIZE_OPTIONS = CATEGORY_OPTIONS.filter((o) => o.value !== 'all');

/**
 * Categories whose fix is Service Compression's code. Sending an item here
 * REMOVES it from this tenant's queue — the platform audience rule hides it
 * from everyone below SUPER_ADMIN — so it is a one-way door for the person
 * choosing it, and it must not happen silently.
 *
 * Mirrors agent-feedback/audience.ts in sc-platform; the API is the authority
 * and rejects a hidden row regardless, this only makes the consequence visible
 * BEFORE the click.
 */
const PLATFORM_ONLY_CATEGORIES: string[] = ['code_bug', 'model_behavior'];

const StatusBadge = ({ status }: { status: FeedbackItem['status'] }) =>
  status === 'resolved' ? (
    <Badge variant="success" className="inline-flex items-center gap-1.5">
      <CheckIcon />
      Resolved
    </Badge>
  ) : status === 'noise' ? (
    <Badge variant="neutral">Noise</Badge>
  ) : status === 'praise' ? (
    <Badge variant="neutral">Praise</Badge>
  ) : null;

const ClosedNote = ({ item }: { item: FeedbackItem }) => {
  if (!item.resolutionNote && !item.closedAt) return null;
  return (
    <div className="flex flex-col gap-1 rounded-md border border-outline bg-surface px-3 py-2.5">
      <span className="text-xs font-semibold uppercase tracking-[0.06em] text-ink-subtle">
        {item.status === 'resolved' ? 'What we changed' : 'Why it’s noise'}
      </span>
      {item.resolutionNote && (
        <p className="text-sm leading-[1.45] text-ink">{item.resolutionNote}</p>
      )}
      {item.closedAt && (
        <span className="text-xs text-ink-subtle">
          Closed {formatDay(item.closedAt)}
          {item.closedByName ? ` by ${item.closedByName}` : ''}
        </span>
      )}
    </div>
  );
};

const FeedbackCard = ({
  item,
  onClose,
}: {
  item: FeedbackItem;
  onClose: (item: FeedbackItem, as: CloseAs) => void;
}) => {
  const recategorize = useRecategorizeLogic();
  const reopen = useSetFeedbackStatusLogic();
  const { data: me } = useMe();
  const keepsSeeingIt = (me?.user.roles ?? []).includes('SUPER_ADMIN');
  // KB-pinned callers (knowledge.view without feedback.view) see only KB rows,
  // so any new category hands the item to the full feedback admins.
  const knowledgeOnly = me ? !canTriageFeedback(me.user) : true;
  const [confirming, setConfirming] = useState<string | null>(null);
  const isOpen = item.status === 'open';

  const apply = (category: FeedbackItem['triageCategory']) => {
    if (!category) return;
    void recategorize.mutateAsync({ feedbackId: item.id, category }).catch(() => undefined);
  };

  return (
    <Card padding="none" className="px-5 py-[18px]">
      <div className="flex flex-wrap justify-between gap-4">
        <div className="flex min-w-0 max-w-[78ch] flex-[1_1_420px] flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {isOpen && item.triageSeverity && (
              <span
                className={`rounded px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${severityTone(item.triageSeverity)}`}
              >
                {item.triageSeverity}
              </span>
            )}
            <StatusBadge status={item.status} />
            <Badge variant="neutral">{categoryLabel(item.triageCategory)}</Badge>
            <span className="inline-flex items-center gap-1.5 text-xs text-ink-subtle">
              <ThumbIcon rating={item.rating} />
              Received {formatDayTime(item.createdAt)}
            </span>
          </div>
          <p className="text-[15px] font-semibold leading-[1.35] text-ink">{quote(item)}</p>
          {isOpen ? (
            <>
              {item.triageSummary && (
                <p className="text-sm leading-[1.45] text-ink-muted">{item.triageSummary}</p>
              )}
              {item.resolutionNote && (
                <p className="text-xs italic text-ink-subtle">Note: {item.resolutionNote}</p>
              )}
            </>
          ) : (
            <ClosedNote item={item} />
          )}
          <TranscriptView conversationId={item.conversationId} messageId={item.messageId} />
        </div>
        {isOpen ? (
          <div className="flex w-[190px] flex-none flex-col gap-2">
            {me && (
              <Select
                aria-label="Re-categorize feedback"
                options={RECATEGORIZE_OPTIONS}
                value={item.triageCategory ? [item.triageCategory] : []}
                placeholder="Categorize…"
                searchable={false}
                disabled={recategorize.isPending}
                onChange={(picked) => {
                  const category = picked[0] as FeedbackItem['triageCategory'];
                  if (!category || category === item.triageCategory) return;
                  // Warn only when the choice makes the item vanish for THIS
                  // person — a SUPER_ADMIN keeps seeing it either way.
                  const leavesTheirList =
                    knowledgeOnly ||
                    (PLATFORM_ONLY_CATEGORIES.includes(category) && !keepsSeeingIt);
                  if (leavesTheirList) {
                    setConfirming(category);
                    return;
                  }
                  apply(category);
                }}
              />
            )}
            <Button variant="secondary" size="sm" onClick={() => onClose(item, 'resolved')}>
              <span className="inline-flex items-center gap-1.5">
                <span className="text-success">
                  <CheckIcon />
                </span>
                Resolve
              </span>
            </Button>
            <Button variant="secondary" size="sm" onClick={() => onClose(item, 'noise')}>
              Dismiss
            </Button>
          </div>
        ) : (
          item.status !== 'praise' && (
            <div className="flex flex-none flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={reopen.isPending}
                onClick={() =>
                  void reopen
                    .mutateAsync({ feedbackId: item.id, status: 'open' })
                    .catch(() => undefined)
                }
              >
                Reopen
              </Button>
            </div>
          )
        )}
      </div>
      {(recategorize.error ?? reopen.error) && (
        <p className="mt-2 text-xs font-semibold text-danger">
          {(recategorize.error ?? reopen.error)?.message}
        </p>
      )}
      <Modal
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
      >
        <Modal.Header>
          <Modal.Title>
            {knowledgeOnly
              ? 'Hand this to your feedback admins?'
              : 'Send this to Service Compression?'}
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          {knowledgeOnly ? (
            <p className="text-sm text-ink-muted">
              Changing the category <b>moves it out of your list</b>. Your feedback admins pick it
              up from there, and you won't see it here afterwards.
            </p>
          ) : (
            <>
              <p className="text-sm text-ink-muted">
                Marking this as a product issue hands it to Service Compression to fix, and{' '}
                <b>removes it from your queue</b>. You won't be able to see it or change it back
                afterwards.
              </p>
              <p className="mt-3 text-sm text-ink-muted">
                Choose this when the assistant itself misbehaved. If the answer was wrong because a
                guide is out of date, use <b>Knowledge base</b> instead. That stays with you.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirming(null)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              apply(confirming as FeedbackItem['triageCategory']);
              setConfirming(null);
            }}
          >
            {knowledgeOnly ? 'Move it' : 'Send it'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

const conversationLink = (item: FeedbackItem): string =>
  `/feedback/conversation/${encodeURIComponent(item.conversationId)}?m=${encodeURIComponent(item.messageId)}`;

const PraiseTable = ({ items, total }: { items: FeedbackItem[]; total: number | undefined }) => (
  <div className="flex flex-col gap-3.5">
    <p className="text-sm text-ink-muted">
      Thumbs up never need a review, so they skip the queue and land here.
    </p>
    <Card padding="none" className="overflow-x-auto">
      <Table.Root className="min-w-[640px]">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Received</Table.HeaderCell>
            <Table.HeaderCell>Their comment</Table.HeaderCell>
            <Table.HeaderCell>What they asked</Table.HeaderCell>
            <Table.HeaderCell align="right">
              <span className="sr-only">Link</span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {items.map((item) => (
            <Table.Row key={item.id}>
              <Table.Cell className="whitespace-nowrap align-top text-ink-muted">
                {formatDayTime(item.createdAt)}
              </Table.Cell>
              <Table.Cell className="align-top">
                {item.comment ? (
                  <span className="font-semibold text-ink">“{item.comment}”</span>
                ) : (
                  <span className="text-ink-subtle">No comment</span>
                )}
              </Table.Cell>
              <Table.Cell className="align-top text-ink-muted">{item.question ?? ''}</Table.Cell>
              <Table.Cell align="right" className="whitespace-nowrap align-top">
                <Link
                  to={conversationLink(item)}
                  className="text-[13px] text-link underline-offset-2 hover:underline"
                >
                  Conversation
                </Link>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Card>
    {total !== undefined && total > items.length && (
      <p className="text-[13px] text-ink-subtle">
        Showing {items.length} of {total}
      </p>
    )}
  </div>
);

const StatusTabs = ({
  tab,
  counts,
  onChange,
}: {
  tab: FeedbackStatus;
  counts: FeedbackCounts | undefined;
  onChange: (tab: FeedbackStatus) => void;
}) => (
  <div role="tablist" aria-label="Status" className="flex flex-wrap gap-1">
    {TABS.map((t) => {
      const selected = tab === t.id;
      return (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={selected}
          onClick={() => onChange(t.id)}
          className={`-mb-px inline-flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-sm transition-colors ${
            selected
              ? 'border-primary font-semibold text-ink'
              : 'border-transparent font-medium text-ink-muted hover:text-ink'
          }`}
        >
          {t.label}
          {counts && (
            <span
              className={`rounded-full px-2 py-px text-xs ${
                selected
                  ? 'bg-primary font-semibold text-white'
                  : 'bg-surface-hover font-medium text-ink-muted'
              }`}
            >
              {counts[t.id]}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

const EMPTY_COPY: Record<FeedbackStatus, string> = {
  open: 'Nothing waiting. New thumbs down feedback lands here after triage.',
  resolved: 'Nothing has been resolved yet.',
  noise: 'Nothing has been dismissed yet.',
  praise: 'No praise yet.',
};

export const FeedbackPage = () => {
  const meQuery = useMe();
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t.id === params.get('status'))?.id ?? 'open';
  const category = (CATEGORY_OPTIONS.find((o) => o.value === params.get('category'))?.value ??
    'all') as FeedbackCategoryFilter;
  // Shareable views: defaults stay out of the URL.
  const setParam = (key: string, value: string, defaultValue: string) => {
    setParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        if (value === defaultValue) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };
  const listQuery = useFeedbackListLogic(tab, category);
  const countsQuery = useFeedbackCountsLogic(category);
  const [closing, setClosing] = useState<{ item: FeedbackItem; as: CloseAs } | null>(null);

  if (meQuery.isLoading) {
    return (
      <AppShell>
        <Skeleton className="mb-2 h-7 w-48" />
        <Skeleton className="mb-6 h-4 w-96" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </AppShell>
    );
  }

  const items = listQuery.data?.feedback ?? [];
  const canTriage = meQuery.data ? canTriageFeedback(meQuery.data.user) : false;

  return (
    <AppShell>
      <div className="flex flex-col gap-[18px]">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-2xl font-bold tracking-[-0.02em] text-ink">Feedback</h2>
          <p className="max-w-[72ch] text-sm text-ink-muted">
            Every thumbs up and thumbs down people leave in chat. Open items need someone to look at
            them. Close each one as resolved or noise, with a note.
          </p>
        </div>

        {listQuery.error && (
          <Banner variant="danger">
            <Banner.Body>
              <Banner.Title>{listQuery.error.message}</Banner.Title>
            </Banner.Body>
          </Banner>
        )}

        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-outline">
          <StatusTabs
            tab={tab}
            counts={countsQuery.data}
            onChange={(t) => setParam('status', t, 'open')}
          />
          <div className="flex items-center gap-2 pb-2">
            {canTriage ? (
              <>
                <span
                  id="category-label"
                  className="text-xs font-semibold uppercase tracking-[0.06em] text-ink-subtle"
                >
                  Category
                </span>
                <div className="w-[190px]">
                  <Select
                    aria-labelledby="category-label"
                    options={CATEGORY_OPTIONS}
                    value={[category ?? 'all']}
                    onChange={(v) => setParam('category', v[0] ?? 'all', 'all')}
                    searchable={false}
                  />
                </div>
              </>
            ) : (
              // The API pins these callers to knowledge_base rows; a filter that
              // can't widen anything would just be a lie.
              <Badge variant="neutral">Knowledge Base items</Badge>
            )}
          </div>
        </div>

        {listQuery.isLoading ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : items.length === 0 ? (
          <Card padding="md">
            <p className="text-sm text-ink-muted">{EMPTY_COPY[tab]}</p>
          </Card>
        ) : tab === 'praise' ? (
          <PraiseTable items={items} total={countsQuery.data?.praise} />
        ) : (
          <div className="flex flex-col gap-3.5">
            {items.map((item) => (
              <FeedbackCard
                key={item.id}
                item={item}
                onClose={(target, as) => setClosing({ item: target, as })}
              />
            ))}
          </div>
        )}
      </div>

      <CloseDialog target={closing} onClose={() => setClosing(null)} />
    </AppShell>
  );
};
